import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { OperationEvent, OperationsSnapshot, TaskStatus } from "../domain/operations";
import { InMemoryOperationsRepository, mergeImportedEvents, type NewWorkInput, type OperationsRepository } from "../data/operationsRepository";
import { demoOperations } from "../data/demoOperations";

const repository: OperationsRepository = new InMemoryOperationsRepository(
  mergeImportedEvents(structuredClone(demoOperations)),
);

interface OperationsContextValue {
  snapshot: OperationsSnapshot | null;
  toast: string | null;
  setTaskStatus(taskId: string, status: TaskStatus, blockerNote?: string): void;
  resolveAttention(attentionId: string): void;
  addWork(input: NewWorkInput): void;
  addEvent(event: OperationEvent): Promise<OperationsSnapshot>;
  addBackendEvent(event: OperationEvent): Promise<OperationsSnapshot>;
  announce(message: string): void;
}

const OperationsContext = createContext<OperationsContextValue | null>(null);

export function OperationsProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<OperationsSnapshot | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    void repository.getSnapshot().then(setSnapshot);
  }, []);

  const announce = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((current) => current === message ? null : current), 2400);
  }, []);

  const value = useMemo<OperationsContextValue>(() => ({
    snapshot,
    toast,
    setTaskStatus(taskId, status, blockerNote) {
      void repository.setTaskStatus(taskId, status, blockerNote).then(setSnapshot);
      announce(status === "complete" ? "Task completed" : status === "in_progress" ? "Task started" : status === "blocked" ? "Blocker reported to operations" : "Task updated");
    },
    resolveAttention(attentionId) {
      void repository.resolveAttention(attentionId).then(setSnapshot);
      announce("Attention item resolved");
    },
    addWork(input) {
      void repository.addWork(input).then(setSnapshot);
      announce("Work added to Today");
    },
    addEvent(event) {
      return repository.addEvent(event).then((next) => {
        setSnapshot(next);
        announce("Event created from invoice import");
        return next;
      });
    },
    addBackendEvent(event) {
      return repository.addBackendEvent(event).then((next) => {
        setSnapshot(next);
        return next;
      });
    },
    announce,
  }), [snapshot, toast, announce]);

  return <OperationsContext.Provider value={value}>{children}</OperationsContext.Provider>;
}

export function useOperations() {
  const context = useContext(OperationsContext);
  if (!context) throw new Error("useOperations must be used within OperationsProvider");
  return context;
}
